import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { STATIC } from '../../lib/queryPresets';
import { tasksKeys } from './useTasks';
import {
  listTaskTags,
  createTaskTag,
  updateTaskTag,
  deleteTaskTag,
  getTaskTagsFor,
  setTaskTags,
} from '../../services/tasks/taskTagService';

// ============================================================
// Query keys
// ============================================================

export const taskTagsKeys = {
  all: ['task-tags'] as const,
  list: () => ['task-tags', 'list'] as const,
  forTasks: (ids: string[]) => ['task-tags', 'for', ids] as const,
};

// ============================================================
// Hooks — Tag list
// ============================================================

export function useTaskTags() {
  return useQuery({
    ...STATIC,
    queryKey: taskTagsKeys.list(),
    queryFn: listTaskTags,
  });
}

// ============================================================
// Hooks — Mutations
// ============================================================

export function useCreateTaskTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ name, color }: { name: string; color: string }) =>
      createTaskTag(name, color),
    onSuccess: () => qc.invalidateQueries({ queryKey: taskTagsKeys.all }),
  });
}

export function useUpdateTaskTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { name?: string; color?: string } }) =>
      updateTaskTag(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: taskTagsKeys.all }),
  });
}

export function useDeleteTaskTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteTaskTag(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: taskTagsKeys.all }),
  });
}

// ============================================================
// Hooks — Per-task tags
// ============================================================

export function useTaskTagsFor(taskIds: string[]) {
  return useQuery({
    ...STATIC,
    queryKey: taskTagsKeys.forTasks(taskIds),
    queryFn: () => getTaskTagsFor(taskIds),
    enabled: taskIds.length > 0,
  });
}

export function useSetTaskTags() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, tagIds }: { taskId: string; tagIds: string[] }) =>
      setTaskTags(taskId, tagIds),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: taskTagsKeys.all });
      qc.invalidateQueries({ queryKey: tasksKeys.all });
    },
  });
}
